import { useState, useEffect, useCallback } from "react";
import apiClient from "../api/apiClient";
import apiPaths from "../api/endpoints";

export interface TrooStory {
  documentId: string;
  name: string;
  level: string;
  order: number;
  focusSkills: string;
  theme: string;
  plot: string;
  ageGroup: string;
  estimatedTime: string;
  storyId: string;
  status: string;
}

export interface TrooStoriesCompetency {
  selCompetency: string;
  stories: TrooStory[];
}

interface TrooStoriesApiResponse {
  success: boolean;
  data: TrooStoriesCompetency[];
}

interface UseGetTrooStoriesParams {
  schoolHash: string;
  teacherId: string;
  complexity: string;
  enabled?: boolean;
}

export function useGetTrooStories({
  schoolHash,
  teacherId,
  complexity,
  enabled = true,
}: UseGetTrooStoriesParams) {
  const [data, setData] = useState<TrooStoriesCompetency[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStories = useCallback(async () => {
    if (!schoolHash || !teacherId) return;
    try {
      setLoading(true);
      setError(null);
      const res = await apiClient.get<TrooStoriesApiResponse>(
        `${apiPaths.GET_TROO_STORIES}/${schoolHash}/${teacherId}`,
        { params: { complexity } }
      );
      setData(res.data?.data || []);
    } catch (err: any) {
      setError(
        err?.response?.data?.message || err?.message || "Failed to fetch stories"
      );
    } finally {
      setLoading(false);
    }
  }, [schoolHash, teacherId, complexity]);

  useEffect(() => {
    if (enabled) fetchStories();
  }, [fetchStories, enabled]);

  return { data, loading, error, refetch: fetchStories };
}
